import { ApolloLink } from '@apollo/client'
import { CombinedGraphQLErrors, toErrorLike } from '@apollo/client/errors'
import { getMainDefinition, print } from '@apollo/client/utilities'
import { Kind, OperationTypeNode } from 'graphql'
import { createClient } from 'graphql-sse'
import type { Client, ClientOptions, EventListeners } from 'graphql-sse'
import { Observable } from 'rxjs'

/**
 * Connection events for one subscription stream. `graphql-sse` calls
 * `connecting` before it opens a request, and `connected` after the server
 * accepts it. Both report whether the stream broke before, so a caller can
 * tell a first connect from a reconnect. {@link SSELink} reports a stream it
 * opens again as a reconnect.
 */
export type RealTimeConnectionListeners = EventListeners<false>

declare module '@apollo/client' {
  interface DefaultContext {
    /**
     * Connection events for one subscription. {@link SSELink} hands them to
     * `graphql-sse`. Set them with `useBufferRealTimeSub`.
     */
    realTimeConnectionListeners?: RealTimeConnectionListeners
  }
}

const DEFAULT_RECONNECT_BASE_DELAY_MS = 1_000
const DEFAULT_RECONNECT_MAX_DELAY_MS = 30_000

/**
 * Error codes that end a subscription for good. The server sends them in the
 * stream, with HTTP 200, and then completes it. A reconnect cannot change any
 * of them, so the link errors the observable instead of opening the stream
 * again: a document the schema rejects stays rejected, and a reader the
 * server denied stays denied. Every other error, `UNAUTHENTICATED` included,
 * keeps the reconnect: a fresh session token can make the next open succeed.
 */
const TERMINAL_ERROR_CODES: ReadonlySet<string> = new Set([
  'GRAPHQL_VALIDATION_FAILED',
  'FORBIDDEN',
  'ORG_REQUIRES_2FA_SETUP',
])

/**
 * Options for {@link SSELink}. `singleConnection` is absent on purpose: the
 * link pins the transport to distinct-connections mode.
 */
export type SSELinkOptions = Omit<ClientOptions<false>, 'singleConnection'> & {
  /**
   * The wait ceiling for the first reconnect, in milliseconds. The ceiling
   * doubles for each further reconnect, up to `reconnectMaxDelayMs`.
   */
  reconnectBaseDelayMs?: number
  /**
   * The wait ceiling no reconnect goes over, in milliseconds. A stream that
   * sends no event counts as healthy after it stays connected for this time.
   */
  reconnectMaxDelayMs?: number
}

/** `true` for a subscription, the one operation kind {@link SSELink} carries. */
export function isSubscriptionOperation(
  operation: ApolloLink.Operation,
): boolean {
  const definition = getMainDefinition(operation.query)

  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.SUBSCRIPTION
  )
}

/**
 * A terminating Apollo link that runs GraphQL subscriptions over Server-Sent
 * Events.
 *
 * The link pins `graphql-sse` to distinct-connections mode. Each subscription
 * is one POST request with an `application/json` body, and that request stays
 * open for the stream. Single-connection mode instead sends `PUT` and `GET`,
 * which the api-gateway rejects in production and staging. See
 * `services/api-gateway/src/graphql/middlewares/ensureJsonType.ts`.
 *
 * One request for each subscription also means the request ends as soon as the
 * caller unsubscribes.
 *
 * A stream of ours is never meant to end while the caller listens. A clean end
 * from the server means the pod goes away, so the link opens the subscription
 * again after a wait and the caller lands on a healthy pod. The connection
 * listeners see that new stream as a reconnect.
 *
 * Each end doubles the wait. The wait drops back to the base once a stream
 * proves healthy, which it does when it sends an event, or when it stays
 * connected for `reconnectMaxDelayMs`.
 *
 * `graphql-sse` retries a network failure on its own. An error it reports
 * after those retries ends the Apollo observable, and the link opens nothing
 * again. A stream error with a code in {@link TERMINAL_ERROR_CODES} ends the
 * observable the same way: the server denied the reader, and the complete
 * that follows must not read as a lost pod.
 */
export class SSELink extends ApolloLink {
  private readonly client: Client<false>
  private readonly reconnectBaseDelayMs: number
  private readonly reconnectMaxDelayMs: number

  constructor({
    reconnectBaseDelayMs = DEFAULT_RECONNECT_BASE_DELAY_MS,
    reconnectMaxDelayMs = DEFAULT_RECONNECT_MAX_DELAY_MS,
    ...clientOptions
  }: SSELinkOptions) {
    super()
    this.client = createClient({ ...clientOptions, singleConnection: false })
    this.reconnectBaseDelayMs = reconnectBaseDelayMs
    this.reconnectMaxDelayMs = reconnectMaxDelayMs
  }

  /**
   * The wait before reconnect number `count`, in milliseconds. The ceiling
   * doubles for each reconnect up to the cap, and each wait is a random point
   * in the upper half of the ceiling, so the clients of one pod come back
   * apart from each other.
   */
  private reconnectDelay(count: number): number {
    const ceiling = Math.min(
      this.reconnectMaxDelayMs,
      this.reconnectBaseDelayMs * 2 ** count,
    )

    return ceiling / 2 + Math.random() * (ceiling / 2)
  }

  public request(
    operation: ApolloLink.Operation,
  ): Observable<ApolloLink.Result> {
    const request = {
      operationName: operation.operationName,
      query: print(operation.query),
      variables: operation.variables,
      extensions: operation.extensions,
    }
    const listeners: RealTimeConnectionListeners | undefined =
      operation.getContext().realTimeConnectionListeners

    return new Observable<ApolloLink.Result>((observer) => {
      let disposeStream: (() => void) | undefined
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined
      let healthyTimer: ReturnType<typeof setTimeout> | undefined
      let reconnectCount = 0
      let streamReconnected = false
      let unsubscribed = false

      const stopHealthyTimer = (): void => {
        clearTimeout(healthyTimer)
        healthyTimer = undefined
      }

      const open = (): void => {
        disposeStream = this.client.subscribe<
          Record<string, unknown>,
          Record<string, unknown>
        >(
          request,
          {
            next: (result): void => {
              stopHealthyTimer()
              // A denial of the stream itself arrives with no data. An event
              // that carries data next to an error is a partial result of a
              // live stream, and the stream goes on.
              if (
                result.data == null &&
                result.errors?.some((error) =>
                  TERMINAL_ERROR_CODES.has(String(error.extensions?.code)),
                )
              ) {
                observer.error(new CombinedGraphQLErrors(result))
                return
              }
              if (result.data != null) reconnectCount = 0
              // Only the keys the server sent. Apollo reads
              // `result.extensions[symbol]` whenever the key is present, so
              // an `extensions: undefined` throws and ends the subscription.
              observer.next({
                data: result.data,
                ...(result.errors !== undefined && { errors: result.errors }),
                ...(result.extensions !== undefined && {
                  extensions: result.extensions,
                }),
              })
            },
            error: (error): void => {
              stopHealthyTimer()
              observer.error(toErrorLike(error))
            },
            complete: (): void => {
              stopHealthyTimer()
              scheduleReconnect()
            },
          },
          {
            ...listeners,
            connecting: (reconnecting): void => {
              listeners?.connecting?.(reconnecting || streamReconnected)
            },
            connected: (reconnected): void => {
              // A pod that goes away accepts the request and ends the stream
              // at once. A reset here would hold the wait at the base for the
              // whole rollout, so the stream must stay open to earn one.
              stopHealthyTimer()
              healthyTimer = setTimeout((): void => {
                healthyTimer = undefined
                reconnectCount = 0
              }, this.reconnectMaxDelayMs)
              listeners?.connected?.(reconnected || streamReconnected)
            },
          },
        )
      }

      const scheduleReconnect = (): void => {
        // `graphql-sse` completes the sink after the caller disposes the
        // stream as well, and that stream must stay closed.
        if (unsubscribed) {
          return
        }

        streamReconnected = true
        listeners?.connecting?.(true)
        reconnectTimer = setTimeout((): void => {
          reconnectTimer = undefined
          open()
        }, this.reconnectDelay(reconnectCount++))
      }

      open()

      return (): void => {
        unsubscribed = true
        stopHealthyTimer()
        clearTimeout(reconnectTimer)
        disposeStream?.()
      }
    })
  }
}
